import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, Mail, Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1779209239383-98d41b31c446?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwY2FyfGVufDB8fHx8MTc4NTk1MDU3NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md mc-slide-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-[#4F46E5] border-2 border-slate-900 flex items-center justify-center nb-shadow">
              <Truck className="text-white" strokeWidth={2.5} size={26} />
            </div>
            <div>
              <h1 className="font-heading font-black text-2xl leading-none tracking-tight">
                Mobicars<span className="text-[#4F46E5]">VSD</span>
              </h1>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mt-1">
                Gestion de flotte
              </p>
            </div>
          </div>

          <h2 className="font-heading text-4xl font-black tracking-tight mb-2">Bon retour 👋</h2>
          <p className="text-slate-500 font-medium mb-8">
            Connectez-vous pour suivre vos véhicules, contrôles et binômes.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-1.5 block">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} strokeWidth={2.5} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.fr"
                  className="nb-input w-full pl-11 pr-4 py-3 font-medium"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-1.5 block">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} strokeWidth={2.5} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="nb-input w-full pl-11 pr-4 py-3 font-medium"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm font-bold text-red-600 bg-red-50 border-2 border-red-500 rounded-xl px-4 py-2.5"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#4F46E5] text-white font-bold py-3.5 rounded-xl nb-btn disabled:opacity-60"
            >
              {loading ? "Connexion..." : "Se connecter"}
              {!loading && <ArrowRight strokeWidth={2.5} size={20} />}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-slate-500 text-sm font-medium">
            <ShieldCheck size={18} strokeWidth={2.5} className="text-[#4ADE80]" />
            Connexion sécurisée · sessions chiffrées
          </div>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:block relative border-l-2 border-slate-900">
        <img src={SIDE_IMG} alt="Véhicule moderne" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#4F46E5]/20" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="nb-card p-6 bg-white/95 backdrop-blur mc-pop">
            <p className="font-heading font-black text-xl mb-1">Toute votre flotte, un coup d'œil.</p>
            <p className="text-slate-600 font-medium text-sm">
              Contrôles techniques, vidanges, binômes et indisponibilités — avec alertes couleur et un assistant intelligent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
